import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { UserRow } from '../../../core/models/api.models';

@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [DatePipe],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.scss',
})
export class UsersListComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<UserRow[]>([]);
  readonly page = signal(1);
  readonly limit = signal(20);
  readonly total = signal(0);
  readonly unlockingId = signal<number | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(page = this.page()): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getUsers(page, this.limit()).subscribe({
      next: (data) => {
        this.rows.set(data.rows || []);
        this.page.set(data.page);
        this.limit.set(data.limit);
        this.total.set(data.total);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load users');
        this.loading.set(false);
      },
    });
  }

  fullName(user: UserRow): string {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—';
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit()));
  }

  prev(): void {
    if (this.page() > 1) {
      this.load(this.page() - 1);
    }
  }

  next(): void {
    if (this.page() < this.totalPages()) {
      this.load(this.page() + 1);
    }
  }

  open(user: UserRow): void {
    void this.router.navigate(['/users', user.id]);
  }

  isLoginLocked(user: UserRow): boolean {
    return Boolean(user.is_locked || user.is_permanently_locked || user.is_temporarily_locked);
  }

  lockLabel(user: UserRow): string {
    if (user.is_permanently_locked) return 'Blocked';
    if (user.is_temporarily_locked) return 'Temp lock';
    return '—';
  }

  unblock(user: UserRow, event: Event): void {
    event.stopPropagation();
    if (!this.isLoginLocked(user) || this.unlockingId() != null) return;
    if (!confirm(`Unblock ${this.fullName(user)} so they can sign in again?`)) return;

    this.unlockingId.set(user.id);
    this.error.set(null);
    this.success.set(null);
    this.api.unlockUserLogin(user.id).subscribe({
      next: () => {
        this.unlockingId.set(null);
        this.success.set(`User #${user.id} unblocked`);
        this.load(this.page());
      },
      error: (err: { message?: string }) => {
        this.unlockingId.set(null);
        this.error.set(err?.message || 'Failed to unblock user');
      },
    });
  }
}
